/**
 * /me/* 子门户 API — R3-Sprint5(对接 backend Sprint 3 β 5 endpoint + Sprint 4 R3 字段接通)。
 *
 * 端点:
 * - GET /me/profile          全角色:account_id / role / tenant_id / permissions
 * - GET /me/dashboard        角色分发:role_scope + 三类聚合数据
 * - GET /me/enterprises      ENTERPRISE/MENTOR;其他 403
 * - GET /me/mentees          仅 MENTOR
 * - GET /me/investments      仅 INVESTOR
 *
 * 通知偏好:
 * - GET /me/notifications/preference
 * - PUT /me/notifications/preference
 */

import { apiGet, apiClient } from './client';

export interface MyProfile {
  account_id: string;
  role: string;
  tenant_id: string | null;
  permissions: string[];
}

export interface EnterpriseDashboard {
  role_scope: 'enterprise';
  enterprise: { id: string; tenant_id: string; name: string; credit_code: string | null } | null;
  owned_count: number;
  active_cases: number;
}

export interface MentorDashboard {
  role_scope: 'mentor';
  mentor_count: number;
  served_enterprises: number;
  active_cases: number;
  todo?: string;
}

export interface InvestorDashboard {
  role_scope: 'investor';
  investment_count: number;
  invested_enterprises: number;
}

export interface AdminDashboard {
  role_scope: 'platform' | 'tenant_admin' | 'staff';
  enterprises_total?: number;
  active_cases_total?: number;
  [k: string]: unknown;
}

export type AnyDashboard = EnterpriseDashboard | MentorDashboard | InvestorDashboard | AdminDashboard;

export interface MyEnterprise {
  id: string;
  tenant_id: string;
  name: string;
  credit_code: string | null;
}

export interface PageEnvelope<T> {
  items: T[];
  total: number;
  page: number;
  page_size: number;
  todo?: string;
}

export interface MyMenteeRow {
  assignment_id: string;
  mentor_id: string;
  enterprise_id: string;
  enterprise_name: string | null;
  credit_code: string | null;
  assigned_date: string | null;
  note: string | null;
}

export interface MyInvestmentRow {
  id: string;
  enterprise_id: string;
  enterprise_name: string | null;
  year: number;
  kind: string;
  amount: number;
  paid_in_confirmed: boolean;
}

export interface MyNotificationPreference {
  account_id: string;
  tenant_id: string | null;
  muted_events: string[];
  inapp_enabled: boolean;
  email_enabled: boolean;
  webhook_enabled: boolean;
  updated_at: string | null;
}

export const getMyProfile = (): Promise<MyProfile> => apiGet('/me/profile');
export const getMyDashboard = (): Promise<AnyDashboard> => apiGet('/me/dashboard');

export const listMyEnterprises = (
  page = 1, page_size = 20,
): Promise<PageEnvelope<MyEnterprise>> =>
  apiGet('/me/enterprises', { page, page_size });

export const listMyMentees = (
  page = 1, page_size = 20,
): Promise<PageEnvelope<MyMenteeRow>> =>
  apiGet('/me/mentees', { page, page_size });

export const listMyInvestments = (
  page = 1, page_size = 20,
): Promise<PageEnvelope<MyInvestmentRow>> =>
  apiGet('/me/investments', { page, page_size });

export const getMyNotificationPreference = (): Promise<MyNotificationPreference> =>
  apiGet('/me/notifications/preference');

export async function updateMyNotificationPreference(
  body: Partial<Omit<MyNotificationPreference, 'account_id' | 'tenant_id' | 'updated_at'>>,
): Promise<MyNotificationPreference> {
  const res = await apiClient.put<MyNotificationPreference>('/me/notifications/preference', body);
  return res.data;
}

// ============================================================================
// Sprint42 — ENTERPRISE 自助门户 4 端点
// ============================================================================

export interface MyCaseRow {
  id: string;
  subject_id: string;
  enterprise_name: string | null;
  credit_code: string | null;
  policy_pack_id: string | null;
  policy_pack_name: string | null;
  declared_qualification_id: string | null;
  declared_qualification_name: string | null;
  declare_year: number | null;
  status: string;
  submitted_at: string | null;
  review_result: string | null;
  created_at: string | null;
}

export interface MyMaterialRow {
  id: string;
  subject_id: string;
  material_type_code: string;
  display_name: string;
  file_url: string;
  file_size: number;
  mime_type: string | null;
  status: string;
  issued_date: string | null;
  uploaded_at: string | null;
  uploaded_by_me: boolean;
}

export const listMyCases = (): Promise<{ items: MyCaseRow[]; total: number }> =>
  apiGet('/me/cases');

export const getMyCaseCompliance = (caseId: string): Promise<unknown> =>
  apiGet(`/me/cases/${caseId}/compliance`);

export const listMyCaseMaterials = (
  caseId: string,
): Promise<{ items: MyMaterialRow[]; total: number }> =>
  apiGet(`/me/cases/${caseId}/materials`);

/** 自助上传材料 — multipart FormData,后端 status 默认 uploaded(等企服审) */
export async function uploadMyCaseMaterial(
  caseId: string,
  form: {
    file: File;
    material_type_code: string;
    display_name: string;
    issued_date?: string;
    indicator_code?: string;
  },
): Promise<MyMaterialRow> {
  const fd = new FormData();
  fd.append('file', form.file);
  fd.append('material_type_code', form.material_type_code);
  fd.append('display_name', form.display_name);
  if (form.issued_date) fd.append('issued_date', form.issued_date);
  if (form.indicator_code) fd.append('indicator_code', form.indicator_code);
  const res = await apiClient.post<MyMaterialRow>(
    `/me/cases/${caseId}/materials/upload`,
    fd,
    { headers: { 'Content-Type': 'multipart/form-data' } },
  );
  return res.data;
}

// ============================================================================
// Sprint43 — ENTERPRISE 完整门户 6 新端点
// ============================================================================

// Sprint48 入孵协议字段
export interface MyEnterpriseDetail {
  enterprise: {
    id: string;
    tenant_id: string;
    name: string;
    credit_code: string | null;
    main_business: string | null;
    industry_segment: string | null;
    industry: string | null;
    legal_representative: string | null;
    registered_capital: number | null;
    registered_date: string | null;
    contact_phone: string | null;
    contact_email: string | null;
    address: string | null;
    annual_metrics: Array<{ year?: number; revenue?: number; profit?: number; tax_total?: number; employee_count?: number; [k: string]: unknown }>;
    completeness: {
      filled: number;
      total: number;
      percent: number;
      missing_fields: string[];
    };
  } | null;
  incubation: {
    id?: string;
    status: 'in_incubation' | 'graduated' | 'not_incubated';
    entry_date?: string;
    exit_date?: string | null;
    exit_event?: string | null;
    days_in_incubation?: number | null;
    site_area?: number | null;
    agreement_no?: string | null;
    agreement_end_date?: string | null;
    agreement_url?: string | null;
    agreement_days_left?: number | null;
    agreement_status?: 'expired' | 'expiring_soon' | 'active' | null;
  } | null;
  qualifications: {
    already_have: Array<{
      id: string;
      qualification_id: string;
      code: string;
      name: string;
      category: string;
      certified_at: string | null;
      expires_at: string | null;
      cert_no: string | null;
    }>;
    declaring: Array<{
      case_id: string;
      status: string;
      declare_year: number | null;
      qualification_id: string | null;
      qual_name: string | null;
      qual_code: string | null;
    }>;
    can_apply: Array<{
      id: string;
      code: string;
      name: string;
      category: string;
      issuing_authority: string | null;
      description: string | null;
    }>;
  };
}

export interface MyPolicyPackRow {
  id: string;
  name: string;
  version: string | null;
  owner_scope: 'platform' | 'tenant';
  grants_qualification_id: string | null;
  grants_qualification: { code: string; name: string; category: string } | null;
}

export interface MyContactStaff {
  account_id: string;
  username: string;
  assignment_role: 'primary' | 'secondary';
  assigned_at: string | null;
}

export interface MyContactAdmin {
  account_id: string;
  username: string;
}

export interface MyContacts {
  assigned_staff: MyContactStaff[];
  tenant_admins: MyContactAdmin[];
}

export const getMyEnterpriseDetail = (): Promise<MyEnterpriseDetail> =>
  apiGet('/me/enterprise-detail');

export const listMyAllMaterials = (
  caseId?: string,
): Promise<{ items: MyMaterialRow[]; total: number }> =>
  apiGet('/me/materials', caseId ? { case_id: caseId } : undefined);

export const listMyAvailablePacks = (): Promise<{ items: MyPolicyPackRow[]; total: number }> =>
  apiGet('/me/policy-packs');

export const getMyContacts = (): Promise<MyContacts> => apiGet('/me/contacts');

export async function uploadMyOrphanMaterial(form: {
  file: File;
  material_type_code: string;
  display_name: string;
  issued_date?: string;
  indicator_code?: string;
}): Promise<MyMaterialRow> {
  const fd = new FormData();
  fd.append('file', form.file);
  fd.append('material_type_code', form.material_type_code);
  fd.append('display_name', form.display_name);
  if (form.issued_date) fd.append('issued_date', form.issued_date);
  if (form.indicator_code) fd.append('indicator_code', form.indicator_code);
  const res = await apiClient.post<MyMaterialRow>(
    '/me/materials/upload',
    fd,
    { headers: { 'Content-Type': 'multipart/form-data' } },
  );
  return res.data;
}

export async function submitMyDeclarationIntent(body: {
  qualification_id: string;
  policy_pack_id?: string;
  note?: string;
}): Promise<{ notified_staff_count: number; enterprise_name: string; qualification_name: string }> {
  const res = await apiClient.post<{
    notified_staff_count: number;
    enterprise_name: string;
    qualification_name: string;
  }>('/me/declaration-intent', body);
  return res.data;
}

// Sprint47:我的操作日志
export interface MyAuditLogRow {
  id: string;
  action: string;
  entity_type: string;
  entity_id: string | null;
  before: Record<string, unknown> | null;
  after: Record<string, unknown> | null;
  created_at: string | null;
  request_id: string | null;
}
export const listMyAuditLog = (): Promise<{ items: MyAuditLogRow[]; total: number }> =>
  apiGet('/me/audit-log');

// Sprint47:通知未读数(Header 铃铛 Badge 用)
export const getNotificationsUnreadCount = (): Promise<{ count: number }> =>
  apiGet('/notifications/unread-count');

// Sprint48:用户反馈
export async function submitFeedback(body: {
  category: 'bug' | 'suggestion' | 'question' | 'other';
  content: string;
  contact_info?: string;
}): Promise<{ notified_count: number; category_label: string }> {
  const res = await apiClient.post<{ notified_count: number; category_label: string }>(
    '/me/feedback', body,
  );
  return res.data;
}

// Sprint46:申报意向追踪 + 年度财务上报
export interface MyDeclarationIntentRow {
  id: string;
  submitted_at: string | null;
  qualification_id: string | null;
  qualification_name: string | null;
  policy_pack_id: string | null;
  enterprise_name: string | null;
  note: string;
  status: 'pending' | 'converted';
  status_label: string;
}

export const listMyDeclarationIntents = (): Promise<{ items: MyDeclarationIntentRow[]; total: number }> =>
  apiGet('/me/declaration-intents');

export async function updateMyAnnualMetrics(body: {
  year: number;
  revenue?: number;
  profit?: number;
  tax_total?: number;
  employee_count?: number;
}): Promise<{ year: number; year_data: Record<string, unknown>; is_new_year: boolean }> {
  const res = await apiClient.patch<{
    year: number;
    year_data: Record<string, unknown>;
    is_new_year: boolean;
  }>('/me/enterprise/annual-metrics', body);
  return res.data;
}

/**
 * Sprint44:企业主自助编辑(强制传营业执照)
 * 字段:main_business / industry_segment / industry / legal_representative /
 *       registered_capital / registered_date / contact_phone / contact_email / address
 * 营业执照必传(material_type=business_license,自动归入材料台账供企服 cross-check)
 */
export async function updateMyEnterprise(
  business_license: File,
  fields: {
    main_business?: string;
    industry_segment?: string;
    industry?: string;
    legal_representative?: string;
    registered_capital?: string; // number 字符串
    registered_date?: string;    // yyyy-mm-dd
    contact_phone?: string;
    contact_email?: string;
    address?: string;
  },
): Promise<{ updated_fields: string[]; business_license: MyMaterialRow }> {
  const fd = new FormData();
  fd.append('business_license', business_license);
  Object.entries(fields).forEach(([k, v]) => {
    if (v != null && v !== '') fd.append(k, v);
  });
  const res = await apiClient.patch<{
    updated_fields: string[];
    business_license: MyMaterialRow;
  }>('/me/enterprise', fd, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return res.data;
}
